import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { adminApi } from "../../api/admin.api.js";
import { useResource } from "../../hooks/useResource.js";
import { FormShell } from "../../components/forms/FormShell.jsx";
import { FormSection } from "../../components/forms/FormSection.jsx";
import { SlugField } from "../../components/forms/SlugField.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Textarea } from "../../components/ui/Textarea.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { Switch } from "../../components/ui/Switch.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { PageLoader } from "../../components/ui/Spinner.jsx";
import { GalleryUpload } from "../../components/upload/GalleryUpload.jsx";

const DEFAULTS = {
  name: "",
  slug: "",
  address: "",
  locationGroup: "",
  status: "open",
  linkedDesign: "",
  googleMapsUrl: "",
  matterportUrl: "",
  coordinates: { lat: "", lng: "" },
  description: "",
  openingHours: { mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
  images: [],
  sortOrder: 0,
  isHidden: false,
};

function buildPayload(values) {
  const out = { ...values };
  if (!out.slug) delete out.slug;
  if (!out.linkedDesign) out.linkedDesign = null;
  // Coords come back as empty strings when not set; coerce to null so the
  // backend validator accepts them.
  out.coordinates = {
    lat: out.coordinates?.lat === "" || out.coordinates?.lat == null
      ? null
      : Number(out.coordinates.lat),
    lng: out.coordinates?.lng === "" || out.coordinates?.lng == null
      ? null
      : Number(out.coordinates.lng),
  };
  if (out.sortOrder !== "") out.sortOrder = Number(out.sortOrder);
  return out;
}

export default function DisplayHomeFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === "new" || !id;

  const { detail, create, update } = useResource("display-homes", {
    toastLabel: "display home",
  });
  const { data: existing, isLoading } = detail(isNew ? null : id);

  const designsQuery = useQuery({
    queryKey: ["designs", "list-for-display-homes"],
    queryFn: () => adminApi.list("designs", { limit: 100, status: "published" }),
    staleTime: 5 * 60_000,
  });

  const form = useForm({ defaultValues: DEFAULTS });
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (existing) {
      reset({
        ...DEFAULTS,
        ...existing,
        // Linked design may be populated as an object or kept as an id.
        linkedDesign:
          typeof existing.linkedDesign === "object"
            ? existing.linkedDesign?._id ?? ""
            : existing.linkedDesign ?? "",
        coordinates: {
          lat: existing.coordinates?.lat ?? "",
          lng: existing.coordinates?.lng ?? "",
        },
        openingHours: {
          ...DEFAULTS.openingHours,
          ...(existing.openingHours ?? {}),
        },
        images: existing.images ?? [],
      });
    }
  }, [existing, reset]);

  useEffect(() => {
    document.title = isNew ? "New Display Home · Admin" : "Edit Display Home · Admin";
  }, [isNew]);

  const onSubmit = handleSubmit(async (values) => {
    const payload = buildPayload(values);
    if (isNew) {
      await new Promise((resolve, reject) => {
        create.mutate(payload, {
          onSuccess: (created) => {
            navigate(`/display-homes/${created._id}`);
            resolve();
          },
          onError: reject,
        });
      });
    } else {
      await new Promise((resolve, reject) => {
        update.mutate({ id, data: payload }, { onSuccess: resolve, onError: reject });
      });
    }
  });

  if (!isNew && isLoading) return <PageLoader />;

  const watchedName = watch("name");
  const watchedSlug = watch("slug");

  return (
    <FormShell
      title={isNew ? "New Display Home" : `Edit "${existing?.name ?? ""}"`}
      backTo="/display-homes"
      backLabel="Back to display homes"
      onSubmit={onSubmit}
      primaryAction={
        <Button type="submit" loading={isSubmitting}>
          {isNew ? "Create display home" : "Save changes"}
        </Button>
      }
      secondaryAction={
        <Button type="button" variant="secondary" onClick={() => navigate("/display-homes")}>
          Cancel
        </Button>
      }
      sidebar={
        <>
          <FormSection title="Visibility">
            <Select label="Status" {...register("status")}>
              <option value="open">Open</option>
              <option value="for_sale_lease">For sale / lease</option>
              <option value="ex_display">Ex-display</option>
            </Select>
            <Controller
              control={control}
              name="isHidden"
              render={({ field }) => (
                <Switch
                  label="Hide from public site"
                  hint="Toggle on to keep this display home in the database but hide it from visitors."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Input type="number" label="Sort order" {...register("sortOrder")} />
          </FormSection>

          <FormSection title="Location pin">
            <Input label="Google Maps URL" type="url" {...register("googleMapsUrl")} />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Latitude"
                type="number"
                step="0.000001"
                {...register("coordinates.lat")}
              />
              <Input
                label="Longitude"
                type="number"
                step="0.000001"
                {...register("coordinates.lng")}
              />
            </div>
          </FormSection>

          <FormSection title="Linked design">
            <Select label="Featured design" {...register("linkedDesign")}>
              <option value="">No design linked</option>
              {designsQuery.data?.items?.map((d) => (
                <option key={d._id} value={d._id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </FormSection>

          <FormSection title="3D virtual tour">
            <Input
              label="Matterport URL"
              type="url"
              hint="Adds a 'View 3D Tour' button on this display home's public page."
              {...register("matterportUrl")}
            />
          </FormSection>
        </>
      }
    >
      <FormSection title="Basics">
        <Input
          label="Display home name"
          required
          error={errors.name?.message}
          {...register("name", { required: "Name is required" })}
        />
        <SlugField
          value={watchedSlug}
          onChange={(v) => setValue("slug", v)}
          source={watchedName}
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Input label="Address" {...register("address")} />
          <Input
            label="Location group"
            hint="e.g. Glen Waverley — used to group cards on the public page."
            {...register("locationGroup")}
          />
        </div>
        <Textarea
          label="Description"
          rows={4}
          hint="Short marketing copy shown on the detail page."
          {...register("description")}
        />
      </FormSection>

      <FormSection title="Photos" description="First image is used as the hero on the listing card.">
        <Controller
          control={control}
          name="images"
          render={({ field }) => (
            <GalleryUpload
              value={field.value ?? []}
              onChange={field.onChange}
              subdir="displays"
              hint="Recommend 8–15 photos in 4:3 or 16:9. Drag to reorder."
            />
          )}
        />
      </FormSection>

      <FormSection
        title="Opening hours"
        description="Leave a day blank to show 'Closed' on the public page."
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          <Input label="Monday" placeholder="10am – 5pm" {...register("openingHours.mon")} />
          <Input label="Tuesday" placeholder="10am – 5pm" {...register("openingHours.tue")} />
          <Input label="Wednesday" placeholder="10am – 5pm" {...register("openingHours.wed")} />
          <Input label="Thursday" placeholder="10am – 5pm" {...register("openingHours.thu")} />
          <Input label="Friday" placeholder="10am – 5pm" {...register("openingHours.fri")} />
          <Input label="Saturday" placeholder="11am – 4pm" {...register("openingHours.sat")} />
          <Input label="Sunday" placeholder="Closed" {...register("openingHours.sun")} />
        </div>
      </FormSection>
    </FormShell>
  );
}
